import { motion } from "motion/react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { tourSurfaceVariants } from "./tourSurfaceVariants";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { CaptainAvatar } from "@/features/captain/components/CaptainAvatar";
import { cn } from "@/lib/utils/cn";

type TourPromoCardProps = {
  copyKey: string;
  onStart: () => void;
  onDismiss: () => void;
};

export function TourPromoCard({ copyKey, onStart, onDismiss }: TourPromoCardProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const key = (part: string) => `${copyKey}.${part}`;

  return (
    <motion.section
      aria-labelledby={titleId}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className={cn(
        tourSurfaceVariants(),
        "flex flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-center sm:gap-5",
      )}
    >
      <CaptainAvatar className="size-14 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Badge variant="info" className="w-fit">
          {t(key("badge"))}
        </Badge>
        <h2 id={titleId} className="text-lg font-semibold leading-[22px]">
          {t(key("title"))}
        </h2>
        <p className="text-sm text-muted-foreground">{t(key("body"))}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          {t(key("later"))}
        </Button>
        <Button onClick={onStart}>{t(key("start"))}</Button>
      </div>
    </motion.section>
  );
}
